<template>
  <section
    v-if="items"
    class="slideshow"
    :class="{
      'slideshow--hide-cursor': items.length > 1,
      'slideshow--hide-controls': touchEnabled,
      'slideshow--mobile': isMobile,
      'slideshow--active': mobileActive,
      'slideshow--highlight': includeHighlight
    }"
    @mousemove="controlImage"
    @click="toggleImage"
    v-touch:swipe="swipeImage"
    v-touch:tap="handleDoubleTap">
    <div
      v-if="includeHighlight && highlightSrc && !isMobile"
      class="slideshow__highlight"
      @click.capture.stop="closeItem"
      @mousemove="closeControlItem">
      <img :src="highlightSrc" />
      <button
        class="slideshow__highlight-cursor"
        :style="`left:${cursorPos.x}px; top:${cursorPos.y}px;`"></button>
    </div>
    <ul>
      <li
        v-for="(item, key) in items"
        :key="`image${key}`"
        class="slideshow__item"
        :class="{'slideshow__item--active': activeItem === key}">
        <img :src="item.image.url" />
      </li>
    </ul>
    <button
      v-if="items.length > 1 && !highlightSrc"
      class="slideshow__cursor"
      :class="cursor"
      :style="`left:${cursorPos.x}px; top:${cursorPos.y}px;`">
    </button>
    <p
      class="slideshow__index"
      @mouseenter="showCursor = false"
      @mousleave="showCursor = true">{{ `${(activeItem + 1)} of ${items.length}`}}</p>
    <button
      v-if="isMobile && mobileActive"
      class="slideshow__close"
      @click.capture.stop="mobileActive = !mobileActive"
      v-html="`Close`">
    </button>
  </section>
</template>

<script>
export default {
  name: 'Slideshow',
  props: {
    items: {
      type: Array,
      default: null
    },
    includeHighlight: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeItem: 0,
      cursorPos: {
        x: 400,
        y: 300
      },
      closeCursorPos: {
        x: 400,
        y: 300
      },
      cursor: 'prev',
      mobileActive: false,
      windowWidth: window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      touchEnabled: 'ontouchstart' in window,
      isMobile: false,
      highlightSrc: null
    }
  },
  created() {
    // set is mobile
    this.isMobile = (this.windowWidth < 1024)
  },
  methods: {
    handleDoubleTap() {
      return false
    },
    toggleImage() {
      if (this.isMobile && !this.mobileActive) {
        this.mobileActive = true
        return false
      }

      if (this.cursor === 'prev') this.prevItem()

      if (this.cursor === 'next') this.nextItem()

      if (this.cursor === 'zoom') this.highlightItem()
    },
    swipeImage(direction) {
      // swipe left => prev
      if (direction === 'left') this.prevItem()

      // swipe right => next
      if (direction === 'right') this.nextItem()
    },
    controlImage($evt) {
      let slideshowWidth = $evt.target.clientWidth

      this.cursorPos.x = $evt.offsetX
      this.cursorPos.y = $evt.offsetY
      this.cursor = $evt.offsetX < (slideshowWidth / 3) ? 'prev' : ($evt.offsetX > ((slideshowWidth / 3) * 2) ? 'next' : (this.includeHighlight === true ? 'zoom' : 'dot'))
    },
    closeControlItem($evt) {
      this.closeCursorPos.x = $evt.offsetX
      this.closeCursorPos.y = $evt.offsetY
    },
    prevItem() {
      this.activeItem = (this.activeItem > 0) ? this.activeItem - 1 : this.items.length - 1
    },
    nextItem() {
      this.activeItem = (this.activeItem < this.items.length - 1) ? this.activeItem + 1 : 0
    },
    highlightItem() {
      this.highlightSrc = this.items[this.activeItem].image.url
    },
    closeItem() {
      this.highlightSrc = null
    }
  }
}
</script>

<style lang="scss" scoped>
.slideshow {
  position:relative;
  margin:0 0 #{$site-padding / 2} 0;
  touch-action: manipulation;
  width:100%;
  &--hide-cursor {
    cursor:none;
  }
  &--hide-controls {
    .slideshow__cursor {
      opacity: 0;
      color:transparent;
    }
  }
  &__item {
    display:none;
    width:100%;
    height: calc((100vw - #{$site-padding * 2}) * 0.75);
    max-height: 1024px;

    &--active {
      display:block;
      img {
        opacity:1 !important;
      }
    }

    img {
      width:100%;
      height:100%;
      display:flex;
      object-fit: contain;
      object-position: 0% 0%;
      opacity:0;
      transition: opacity 240ms ease-in-out;
    }
  }

  &:hover {
    .slideshow__cursor {
      display:block;
    }
  }

  &__cursor {
    @include button_resets;
    // background:#000;
    font-family:$maison !important;
    width:74px;
    height:74px;
    position:absolute;
    user-select: none;
    pointer-events: none;
    transform: translateX(-50%) translateY(-50%);
    display:none;
    // cursor:none;

    &:before {
      content:'+';
      font-size:$heading;
      line-height: 1;
      position:absolute;
      transform: translateY(-50%);
      left: 0;
      // cursor:none;
    }

    &.prev {
      &:before {
        content:'←';
      }
    }

    &.dot {
      &:before {
        content:'•';
      }
    }

    &.next {
      &:before {
        content:'→';
      }
    }
  }

  &__index {
    position:absolute;
    bottom:-2.5rem;
    right:0px;
    font-family: $ops-placard;
    pointer-events: none;
  }

  &__close {
    @include button_resets;
    position:absolute;
    bottom:1rem;
    left:1rem;
    font-size:$heading--m;
    line-height:1;
    font-family: $ops-placard;
    padding:0;
    z-index: 100;
  }

  &__highlight {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100;
    overflow: scroll;
    background: #fff;
    // padding: 1rem;
    z-index:5000;

    img {
      width:100vw;
      float: left;
    }

    p {
      position: fixed;
      right:calc(#{$site-padding-lrg} - 8px);
      top:$site-padding;
      font-size: $body;
      line-height: $line-height;
      letter-spacing:2px;
      color:transparent;
      &:hover {
        color:transparent;
      }
      &:after {
        content: '';
        display:block;
        background-image: url('/images/JW_Wordmark.svg');
        background-size:contain;
        width: 2.75rem;
        height: 2.75rem;
        background-repeat: no-repeat;
        position: absolute;
        top: 12px;
      }
    }

    &-cursor {
      @include button_resets;
      font-family:$maison !important;
      width:74px;
      height:74px;
      position:absolute;
      user-select: none;
      pointer-events: none;
      transform: translateX(-50%) translateY(-50%);

      &:before {
        content:'×';
        font-size:$heading;
        line-height: 1;
        position:absolute;
        transform: translateY(-50%) translateX(-50%);
        left: 50%;
      }
    }
  }

  @include mobile {

    &--active {
      width:100vw;
      height:100%;
      overflow:hidden;
      position:fixed;
      background-color:#fff;
      z-index:100;
      top:0px;
      left:0px;
      touch-action: pan-x, pan-y;

      .slideshow__item {
        width:100%;
        height:calc(100vh - 4rem);
        padding:1rem;
        justify-content: center;
        align-items: center;
        max-height:unset;
        img {
          object-position: 50% 50%;
          max-height:80%;
        }

        &--active {
          display:flex;
        }
      }

      .slideshow__index {
        width: 100vw;
        line-height: 1;
        padding: 1rem;
        left:0;
        bottom:0;
        transform: unset;
        background-color:#fff;
        // font-size
      }

      .slideshow__close {
        // font-family: $maison;
        font-size: $body--m;
      }
    }

    &__item {
      height: calc((100vw - #{$site-padding * 2}) * 0.75);
      max-height: 640px;
    }

    &__cursor {
      // display:none !important;
    }

    &__index {
      right: $mobile-padding;
      // left:50%;
      bottom: -2.25rem;
      // transform: translateX(-50%);
      text-align: center;
      font-size: $body--m;
    }
  }
}
</style>
